/**
 * Saving a signature as a PNG.
 *
 * The BFF stores only the record — name, cargo, telefone, e-mail, status. The image itself is
 * never persisted anywhere: it is produced in the browser with `toPng` and handed straight to the
 * mail endpoint. So a download here is a **re-render** of the same template with the same data,
 * not the bytes that were delivered; if the template changes, an old signature downloads in the
 * new template.
 *
 * @module Pages/Signatures
 */

import { toPng } from 'html-to-image';

/** Same ratio the creation screen uses, so the file holds up at retina sizes in an e-mail client. */
const PIXEL_RATIO = 6;

/** `Caio Nunes` → `assinatura-caio-nunes.png`. */
export function buildSignatureFileName(employeeName: string): string {
  const slug = employeeName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `assinatura-${slug || 'sem-nome'}.png`;
}

/** Renders the element to a PNG and hands it to the browser to save. */
export async function downloadSignaturePng(
  element: HTMLElement,
  employeeName: string,
): Promise<void> {
  const dataUrl = await toPng(element, { pixelRatio: PIXEL_RATIO });

  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = buildSignatureFileName(employeeName);
  link.click();
}

/**
 * Renders the element to base64 PNG data, for the send and resend endpoints.
 *
 * Returns the payload **without** the `data:image/png;base64,` prefix that `toPng` produces: the
 * BFF hands this straight to nodemailer as `content` with `encoding: 'base64'`, and the prefix
 * would be decoded as part of the image, corrupting the attachment.
 *
 * Same render as the download, minus the save: the BFF never stored the image, so resending has to
 * produce it again from the record. A signature written before a template change therefore goes
 * out in the current template — the same caveat the download already carries.
 */
export async function renderSignatureBase64(element: HTMLElement): Promise<string> {
  const dataUrl = await toPng(element, { pixelRatio: PIXEL_RATIO });
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}
